/**
 * Theme switching and persistence via cookies.
 * Applies theme tokens as CSS custom properties on the <html> element.
 */

import { COOKIE_KEYS } from "../config/constants";
import {
  type Theme,
  type ThemeTokens,
  BUILT_IN_THEMES,
  TOKEN_TO_CSS_VAR,
} from "../config/themes";
import { getCookie, setCookie } from "./utils/cookies";

function loadCustomThemes(): Theme[] {
  try {
    const saved = getCookie<Theme | null>(COOKIE_KEYS.CUSTOM_THEME, null);
    return saved ? [saved] : [];
  } catch {
    return [];
  }
}

/** Get a theme by its ID, checking built-ins first. */
export function getThemeById(id: string, customThemes: Theme[] = []): Theme | undefined {
  return [...BUILT_IN_THEMES, ...loadCustomThemes(), ...customThemes].find((t) => t.id === id);
}

/** Apply a theme's tokens as CSS custom properties on <html>. */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  root.setAttribute("data-theme", theme.id);
  for (const [key, value] of Object.entries(theme.tokens) as [keyof ThemeTokens, string][]) {
    root.style.setProperty(TOKEN_TO_CSS_VAR[key], value);
  }
}

/** Load the saved theme ID from cookies, apply it, and return the active theme. */
export function loadTheme(customThemes: Theme[] = []): Theme {
  const savedId = getCookie<string>(COOKIE_KEYS.THEME, BUILT_IN_THEMES[0].id);
  const theme = getThemeById(savedId, [...loadCustomThemes(), ...customThemes]) ?? BUILT_IN_THEMES[0];
  applyTheme(theme);
  return theme;
}

/** Save the selected theme ID to cookies and apply it. */
export function selectTheme(themeId: string, customThemes: Theme[] = []): Theme {
  const theme = getThemeById(themeId, customThemes);
  if (!theme) throw new Error(`Unknown theme: ${themeId}`);
  setCookie(COOKIE_KEYS.THEME, themeId);
  applyTheme(theme);
  return theme;
}
